import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, FolderOpen, Upload, ExternalLink } from "lucide-react";

const DOC_TYPES = [["receipt", "Kvitto"], ["supplier_invoice", "Leverantörsfaktura"], ["agreement", "Avtal"], ["other", "Övrigt"]];
const TYPE_LABEL = Object.fromEntries(DOC_TYPES);
const EMPTY = { title: "", doc_type: "receipt", url: "", amount: "", doc_date: "", supplier_id: "", notes: "" };

export default function DocumentsPanel() {
  const [documents, setDocuments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([api.get("/admin/documents"), api.get("/admin/suppliers")]);
      setDocuments(d.data.documents || []);
      setSuppliers((s.data.suppliers || []).filter(x => x.active !== false));
    } catch { toast.error("Kunde inte ladda dokument"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, url: res.data.url || res.data.image_url || "" }));
      toast.success("Fil uppladdad");
    } catch { toast.error("Kunde inte ladda upp filen (bilder stöds — t.ex. foto på kvittot)"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.amount) payload.amount = parseFloat(payload.amount) || null;
      else delete payload.amount;
      if (payload.supplier_id) payload.related_entity_type = "supplier";
      if (payload.supplier_id) payload.related_entity_id = payload.supplier_id;
      await api.post("/admin/documents", payload);
      toast.success("Dokument sparat");
      setDialog(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte spara"); }
    finally { setSaving(false); }
  };

  const supplierName = (id) => suppliers.find(s => s.id === id)?.name || "";
  const fmt = (s) => { try { return new Date(s).toLocaleDateString("sv-SE"); } catch { return ""; } };

  return (
    <div className="max-w-3xl" data-testid="documents-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-playfair text-xl font-bold text-white">Dokument</h2>
          <p className="text-[#C8C8CE] text-xs mt-1">Kvitton, leverantörsfakturor och avtal — underlag för bokföringen. Dokument raderas aldrig.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY); setDialog(true); }} className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Nytt dokument
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" /></div>
      ) : documents.length === 0 ? (
        <div className="bg-[#121212] border border-[#27272A] rounded-xl p-10 text-center">
          <FolderOpen className="w-8 h-8 text-[#3F3F46] mx-auto mb-3" />
          <p className="text-[#C0C0C6] text-sm">Inga dokument ännu. Fota kvittot och ladda upp det här.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map(d => (
            <div key={d.id} className="bg-[#121212] rounded-xl border border-[#27272A] p-4 flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#3B82F6]/10 flex items-center justify-center shrink-0">
                <FolderOpen className="w-4 h-4 text-[#93C5FD]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium">
                  {d.title}
                  <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#27272A]/70 text-[#C0C0C6]">{TYPE_LABEL[d.doc_type] || d.doc_type}</span>
                </p>
                <p className="text-[11px] text-[#9A9AA2] mt-0.5">
                  {[d.amount != null && `${Number(d.amount).toFixed(2)} kr`, d.doc_date,
                    d.supplier_id && supplierName(d.supplier_id), `av ${d.uploaded_by}`, fmt(d.created_at)]
                    .filter(Boolean).join(" · ")}
                </p>
                {d.notes && <p className="text-[11px] text-[#C8C8CE] mt-1">{d.notes}</p>}
              </div>
              {d.url && (
                <a href={d.url} target="_blank" rel="noopener noreferrer"
                  className="text-[#D4B483] hover:text-[#E5CFA5] text-xs inline-flex items-center gap-1 shrink-0">
                  <ExternalLink className="w-3.5 h-3.5" /> Öppna
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Nytt dokument</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Titel *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="t.ex. Kvitto grönsaker v.27" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Typ</Label>
                <select value={form.doc_type} onChange={e => setForm(f => ({ ...f, doc_type: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] rounded-md h-10 text-sm px-2">
                  {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Belopp (kr)</Label>
                <Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Datum på dokumentet</Label>
                <Input type="date" value={form.doc_date} onChange={e => setForm(f => ({ ...f, doc_date: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Leverantör</Label>
                <select value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] rounded-md h-10 text-sm px-2">
                  <option value="">—</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Fil (foto på kvittot) eller länk</Label>
              <div className="flex gap-2">
                <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                  placeholder="https://… eller ladda upp →" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] flex-1" />
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={e => uploadFile(e.target.files?.[0])} />
                  <span className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-[#27272A] bg-[#0A0A0A] text-xs text-[#D4B483] hover:border-[#D4B483]/50">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Ladda upp
                  </span>
                </label>
              </div>
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Anteckning</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] resize-none" />
            </div>
            <Button onClick={save} disabled={saving || uploading || !form.title.trim()}
              className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Spara dokument
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
